import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getServiceRequests, updateServiceRequest, deleteServiceRequest, restoreServiceRequest,
  createWorkOrderFromServiceRequest, createQuoteFromServiceRequest,
} from '../../services/api';
import { PageHeader, StatusBadge, PriorityBadge } from '../../components/Shared';
import { AttachmentList } from '../../components/Attachments';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Trash2, Wrench, FileText } from 'lucide-react';
import { toast } from 'sonner';

const ServiceRequestsAdmin = () => {
  const [list, setList] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();
  const refresh = () => getServiceRequests().then(setList);
  useEffect(() => { refresh(); }, []);

  const update = async (id, status) => {
    try { await updateServiceRequest(id, { status }); await refresh(); toast.success('Request updated'); }
    catch { toast.error('Update failed'); }
  };

  const spawnWorkOrder = async (r) => {
    setBusyId(r.id);
    try {
      const { workOrderId } = await createWorkOrderFromServiceRequest(r.id);
      toast.success(`Work order ${workOrderId} created — schedule it now`);
      await refresh();
      navigate('/admin/work-orders');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create work order');
    } finally {
      setBusyId(null);
    }
  };

  const spawnQuote = async (r) => {
    setBusyId(r.id);
    try {
      const { quoteId } = await createQuoteFromServiceRequest(r.id);
      toast.success(`Draft quote ${quoteId} created — add line items to price it up`);
      await refresh();
      navigate('/admin/quotes');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create quote');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r) => {
    try {
      await deleteServiceRequest(r.id);
      const snapshot = list;
      setList((l) => l.filter(x => x.id !== r.id));
      toast.success(`Work request ${r.id} deleted`, {
        duration: 6000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await restoreServiceRequest(r.id);
              setList(snapshot);
              toast.success('Restored');
            } catch {
              toast.error('Restore failed');
              refresh();
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader title="Work Requests" description="Customer-submitted work requests awaiting action." />
      <div className="space-y-3">
        {list.map(r => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-md p-5 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-xs font-mono text-slate-500">{r.id}</span>
                <PriorityBadge priority={r.priority} />
                <StatusBadge status={r.status} />
                {r.kind === 'quote_request' && (
                  <span
                    className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded"
                    data-testid={`quote-request-badge-${r.id}`}
                  >
                    Quote request
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-slate-900">{r.title}</h3>
              <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{r.description}</p>
              {r.attachments?.length > 0 && <AttachmentList items={r.attachments} />}
              <div className="text-xs text-slate-500 mt-2">
                From {r.customer} · {r.createdAt}
                {r.linkedWorkOrder && <> · Linked WO: <span className="font-mono">{r.linkedWorkOrder}</span></>}
                {r.linkedQuoteId && <> · Linked quote: <span className="font-mono">{r.linkedQuoteId}</span></>}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0 w-40">
              <Select value={r.status} onValueChange={(v) => update(r.id, v)}>
                <SelectTrigger className="w-full h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              {!r.linkedQuoteId && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                  onClick={() => spawnQuote(r)}
                  disabled={busyId === r.id}
                  data-testid={`wr-create-quote-${r.id}`}
                >
                  <FileText className="h-4 w-4 mr-1" /> Create quote
                </Button>
              )}
              {!r.linkedWorkOrder && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                  onClick={() => spawnWorkOrder(r)}
                  disabled={busyId === r.id}
                  data-testid={`wr-create-wo-${r.id}`}
                >
                  <Wrench className="h-4 w-4 mr-1" /> Create work order
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => update(r.id, 'completed')}><CheckCircle2 className="h-4 w-4 mr-1" /> Resolve</Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                onClick={() => remove(r)}
                data-testid={`wr-delete-${r.id}`}
              >
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="p-12 text-center text-slate-500 bg-white border border-slate-200 rounded-md">No work requests yet.</div>}
      </div>
    </div>
  );
};

export default ServiceRequestsAdmin;
